import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Shield, AlertTriangle, Eye, Camera, Mic, Monitor, 
  ChevronDown, ChevronUp, ExternalLink, Search, Filter,
  CheckCircle, XCircle, Clock, User, Download, FileText, 
  Image as ImageIcon, X, Trash2, ChevronLeft, ChevronRight,
  Video, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { UploadQueueWidget } from '../../components/UploadQueueWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Component to handle video playback from base64 data URIs
function VideoClipPlayer({ clip, idx, sessionId }) {
  const videoRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadVideo = async () => {
      try {
        let videoUrl = clip.url;
        
        // If clip data was stripped, fetch it on-demand
        if (clip.has_data && !videoUrl) {
          const token = localStorage.getItem('token');
          const res = await fetch(`${BACKEND_URL}/api/admin/proctoring/sessions/${sessionId}/clip/${clip.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) throw new Error('Failed to fetch clip');
          const clipData = await res.json();
          videoUrl = clipData.url;
        }

        if (!videoUrl) { if (!cancelled) setError(true); return; }

        let url;
        if (videoUrl.startsWith('data:')) {
          // Use fetch to convert data URI to blob
          try {
            const response = await fetch(videoUrl);
            const blob = await response.blob();
            url = URL.createObjectURL(blob);
          } catch {
            // Manual fallback
            const parts = videoUrl.split(',');
            const mimeMatch = parts[0].match(/data:(.*?);/);
            const mime = mimeMatch ? mimeMatch[1] : 'video/webm';
            const raw = atob(parts[1]);
            const arr = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
            url = URL.createObjectURL(new Blob([arr], { type: mime }));
          }
        } else {
          url = videoUrl;
        }
        
        if (!cancelled) {
          blobUrlRef.current = url;
          setBlobUrl(url);
          setLoading(false);
        } else if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Failed to load video clip:', err);
        if (!cancelled) { setError(true); setLoading(false); }
      }
    };

    loadVideo();

    return () => {
      cancelled = true;
      if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [clip.id, clip.url, clip.has_data, sessionId]);

  const handleDownload = () => {
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `violation_${clip.violation_type}_${clip.timestamp || 'clip'}.webm`;
      a.click();
    }
  };

  return (
    <div className="border rounded-lg p-2 bg-gray-50" data-testid={`video-clip-${clip.id || idx}`}>
      <div className="flex items-center justify-between mb-1.5">
        <Badge variant="outline" className="text-xs capitalize">
          {clip.violation_type?.replace(/_/g, ' ')}
        </Badge>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {clip.timestamp ? new Date(clip.timestamp).toLocaleTimeString() : ''}
            {clip.duration_seconds ? ` (${clip.duration_seconds}s)` : ''}
          </span>
          {blobUrl && (
            <button onClick={handleDownload} className="text-blue-500 hover:text-blue-700" title="Download clip">
              <Download className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {error ? (
        <div className="w-full h-24 bg-red-50 rounded flex items-center justify-center text-red-400 text-sm">
          Failed to load video
        </div>
      ) : blobUrl ? (
        <video
          ref={videoRef}
          controls
          preload="auto"
          className="w-full rounded max-h-48 bg-black"
          data-testid={`video-player-${clip.id || idx}`}
          src={blobUrl}
          onError={() => setError(true)}
        />
      ) : (
        <div className="w-full h-24 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-sm">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400 mr-2"></div>
          Loading video...
        </div>
      )}
    </div>
  );
}

function ProctoringDashboard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [threshold, setThreshold] = useState(70);

  // Filter states
  const [tests, setTests] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedTest, setSelectedTest] = useState('all');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');

  // Image viewer
  const [imageModal, setImageModal] = useState(null);

  // Multi-select & delete
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // Photo gallery modal
  const [photoGallery, setPhotoGallery] = useState(null); // { photos: [], currentIndex: 0, title: '' }

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const [testsRes, batchesRes] = await Promise.all([
        api.get('/tests'),
        api.get('/batches')
      ]);
      setTests(testsRes.data || []);
      setBatches(batchesRes.data || []);
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  };

  const hasScopeFilter = selectedBatch !== 'all' || selectedTest !== 'all' || selectedUser !== 'all';

  const fetchSessions = async () => {
    // Don't load the entire proctoring history — require a batch/test/user filter
    if (selectedBatch === 'all' && selectedTest === 'all' && selectedUser === 'all') {
      setSessions([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedTest !== 'all') params.append('test_id', selectedTest);
      if (selectedBatch !== 'all') params.append('batch_id', selectedBatch);
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      const url = `/admin/proctoring/sessions${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load proctoring sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [selectedTest, selectedBatch, selectedUser]);

  // Unique users from sessions for user filter
  const uniqueUsers = useMemo(() => {
    const map = new Map();
    sessions.forEach(s => {
      if (!map.has(s.user_id)) {
        map.set(s.user_id, { id: s.user_id, name: s.user_name, email: s.user_email });
      }
    });
    return Array.from(map.values());
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    return sessions.filter(session => {
      if (statusFilter === 'flagged' && session.trust_score >= threshold) return false;
      if (statusFilter === 'clear' && session.trust_score < threshold) return false;
      if (selectedUser !== 'all' && session.user_id !== selectedUser) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return (
          session.user_name?.toLowerCase().includes(q) ||
          session.user_email?.toLowerCase().includes(q) ||
          session.test_title?.toLowerCase().includes(q) ||
          session.batch_name?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [sessions, statusFilter, threshold, selectedUser, searchTerm]);

  const stats = useMemo(() => ({
    total: sessions.length,
    flagged: sessions.filter(s => s.trust_score < threshold).length,
    clear: sessions.filter(s => s.trust_score >= threshold).length,
    avgScore: sessions.length > 0
      ? Math.round(sessions.reduce((sum, s) => sum + (s.trust_score || 0), 0) / sessions.length)
      : 0
  }), [sessions, threshold]);

  const getTrustScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getTrustGradeBadge = (grade) => {
    const colors = { 'A': 'bg-green-500', 'B': 'bg-blue-500', 'C': 'bg-yellow-500', 'D': 'bg-orange-500', 'F': 'bg-red-500' };
    return colors[grade] || 'bg-gray-500';
  };

  const getViolationIcon = (type) => {
    switch (type) {
      case 'tab_switch': case 'fullscreen_exit': return <Monitor className="w-4 h-4" />;
      case 'no_face': case 'multiple_faces': return <User className="w-4 h-4" />;
      case 'phone_detected': return <AlertTriangle className="w-4 h-4" />;
      case 'looking_away': return <Eye className="w-4 h-4" />;
      case 'voice_detected': return <Mic className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (selectedTest !== 'all') params.append('test_id', selectedTest);
      if (selectedBatch !== 'all') params.append('batch_id', selectedBatch);
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      
      const url = `${BACKEND_URL}/api/admin/proctoring/export/${format}${params.toString() ? '?' + params.toString() : ''}`;
      const token = localStorage.getItem('token');
      
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `proctoring_report.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export');
    }
  };

  const resetFilters = () => {
    setSelectedTest('all');
    setSelectedBatch('all');
    setSelectedUser('all');
    setStatusFilter('all');
    setSearchTerm('');
  };

  // Multi-select helpers
  const toggleSession = (id) => {
    setSelectedSessions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSessions.size === filteredSessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(filteredSessions.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) return;
    if (!window.confirm(`Delete ${selectedSessions.size} proctoring session(s)? This cannot be undone.`)) return;
    
    setDeleting(true);
    try {
      await api.delete('/admin/proctoring/sessions/bulk', { data: { session_ids: Array.from(selectedSessions) } });
      toast.success(`Deleted ${selectedSessions.size} session(s)`);
      setSelectedSessions(new Set());
      fetchSessions();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete sessions');
    } finally {
      setDeleting(false);
    }
  };

  // Photo gallery
  const openPhotoGallery = (photos, userName) => {
    const validPhotos = (photos || []).filter(p => p.url);
    if (validPhotos.length === 0) {
      toast.info('No captured images available for this session');
      return;
    }
    setPhotoGallery({ photos: validPhotos, currentIndex: 0, title: `Captured Photos — ${userName}` });
  };

  if (loading && sessions.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600">Loading proctoring data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="proctoring-dashboard">
        {/* Header + Export/Delete Buttons */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-blue-600" />
              Proctoring Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Monitor test integrity and review flagged sessions</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {selectedSessions.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={deleting}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {deleting ? 'Deleting...' : `Delete (${selectedSessions.size})`}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => handleExport('excel')} data-testid="export-excel-btn">
              <Download className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        {/* Live upload queue health */}
        <div className="flex justify-end">
          <UploadQueueWidget />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="flex-1 min-w-[180px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search name, email, test..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Test Filter */}
              <Select value={selectedTest} onValueChange={setSelectedTest}>
                <SelectTrigger className="w-[180px]" data-testid="test-filter">
                  <SelectValue placeholder="All Tests" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tests</SelectItem>
                  {tests.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Batch Filter */}
              <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                <SelectTrigger className="w-[180px]" data-testid="batch-filter">
                  <SelectValue placeholder="All Batches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batches</SelectItem>
                  {batches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* User Filter */}
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-[180px]" data-testid="user-filter">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {uniqueUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-1" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="flagged">Flagged</SelectItem>
                  <SelectItem value="clear">Clear</SelectItem>
                </SelectContent>
              </Select>

              {/* Threshold */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Threshold:</span>
                <Input
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                  className="w-16 h-9"
                  min={0}
                  max={100}
                />
                <span className="text-xs text-gray-500">%</span>
              </div>

              {/* Reset */}
              {(selectedTest !== 'all' || selectedBatch !== 'all' || selectedUser !== 'all' || statusFilter !== 'all' || searchTerm) && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <X className="w-4 h-4 mr-1" /> Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sessions List */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Proctoring Sessions</CardTitle>
                <CardDescription>
                  {filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''} found
                  {selectedSessions.size > 0 && <span className="ml-2 text-blue-600 font-medium">({selectedSessions.size} selected)</span>}
                  {loading && <span className="ml-2 text-blue-500">Refreshing...</span>}
                </CardDescription>
              </div>
              {filteredSessions.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 hover:text-gray-900" data-testid="select-all-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedSessions.size === filteredSessions.length && filteredSessions.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Select All
                </label>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!hasScopeFilter ? (
              <div className="text-center py-12" data-testid="select-scope-prompt">
                <Filter className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Select a batch or test to load sessions</p>
                <p className="text-gray-400 text-sm mt-1">Sessions are loaded per selection to keep the dashboard fast.</p>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{loading ? 'Loading sessions...' : 'No proctoring sessions found'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`border rounded-lg transition-all ${
                      session.trust_score < threshold ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white'
                    }`}
                    data-testid={`session-${session.id}`}
                  >
                    {/* Session Header Row */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50/50"
                      onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedSessions.has(session.id)}
                            onChange={(e) => { e.stopPropagation(); toggleSession(session.id); }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                            data-testid={`select-session-${session.id}`}
                          />
                          <div className={`w-14 h-14 rounded-full flex flex-col items-center justify-center ${getTrustScoreColor(session.trust_score)}`}>
                            <span className="text-lg font-bold">{Math.round(session.trust_score || 0)}</span>
                            <span className="text-[10px]">Trust</span>
                          </div>
                          {session.gaze_score != null && (
                            <div className={`w-14 h-14 rounded-full flex flex-col items-center justify-center ${getTrustScoreColor(session.gaze_score)}`}
                              data-testid={`gaze-score-${session.id}`}>
                              <span className="text-lg font-bold">{Math.round(session.gaze_score)}</span>
                              <span className="text-[10px]">Gaze</span>
                            </div>
                          )}
                          {session.mobile_connected && (
                            <div className={`w-14 h-14 rounded-full flex flex-col items-center justify-center ${getTrustScoreColor(session.combined_trust_score || session.trust_score)}`}
                              data-testid={`combined-score-${session.id}`}>
                              <span className="text-lg font-bold">{Math.round(session.combined_trust_score || session.trust_score)}</span>
                              <span className="text-[10px]">Combined</span>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-900">{session.user_name}</h3>
                              <Badge className={`${getTrustGradeBadge(session.trust_grade)} text-white text-[10px] px-1.5`}>
                                {session.trust_grade}
                              </Badge>
                              {session.trust_score < threshold && <Badge variant="destructive" className="text-[10px]">Flagged</Badge>}
                            </div>
                            <p className="text-sm text-gray-600">{session.user_email}</p>
                            <div className="flex gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                              <span>{session.test_title}</span>
                              {session.batch_name && session.batch_name !== 'No Batch' && (
                                <span className="px-1.5 py-0 bg-gray-100 rounded">{session.batch_name}</span>
                              )}
                              {session.test_percentage != null && (
                                <span className={`px-1.5 py-0 rounded font-medium ${
                                  session.test_percentage >= 60 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  Score: {session.test_score} ({session.test_percentage}%)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {/* Image links */}
                          <div className="hidden lg:flex items-center gap-1">
                            {session.id_document_url && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setImageModal({ url: session.id_document_url, title: `ID Document — ${session.user_name}` }); }}
                                className="p-1 text-blue-500 hover:bg-blue-50 rounded" title="View ID Document"
                                data-testid={`view-id-doc-${session.id}`}
                              >
                                <ImageIcon className="w-4 h-4" />
                              </button>
                            )}
                            {session.live_photo_url && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setImageModal({ url: session.live_photo_url, title: `Live Photo — ${session.user_name}` }); }}
                                className="p-1 text-green-500 hover:bg-green-50 rounded" title="View Live Photo"
                                data-testid={`view-live-photo-${session.id}`}
                              >
                                <Camera className="w-4 h-4" />
                              </button>
                            )}
                            {session.photos && session.photos.filter(p => p.url).length > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openPhotoGallery(session.photos, session.user_name); }}
                                className="flex items-center gap-0.5 p-1 text-purple-500 hover:bg-purple-50 rounded text-xs font-medium" title="View Captured Photos"
                                data-testid={`view-photos-${session.id}`}
                              >
                                <Camera className="w-4 h-4" />
                                <span>{session.photos.filter(p => p.url).length}</span>
                              </button>
                            )}
                          </div>

                          <Badge variant={session.status === 'completed' ? 'default' : 'secondary'} className="text-[10px]">
                            {session.status}
                          </Badge>

                          {expandedSession === session.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedSession === session.id && (
                      <div className="border-t p-4 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Violations */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Violation Summary</h4>
                            <div className="space-y-2">
                              {[
                                { icon: <Monitor className="w-4 h-4 text-gray-500" />, label: 'Tab Switches', val: session.tab_switch_count },
                                { icon: <Monitor className="w-4 h-4 text-gray-500" />, label: 'Fullscreen Exits', val: session.fullscreen_exit_count },
                                { icon: <User className="w-4 h-4 text-gray-500" />, label: 'No Face Detected', val: session.no_face_detected_count },
                                { icon: <User className="w-4 h-4 text-gray-500" />, label: 'Multiple Faces', val: session.multiple_faces_count },
                                { icon: <Eye className="w-4 h-4 text-gray-500" />, label: 'Looking Away', val: session.looking_away_count },
                                { icon: <Mic className="w-4 h-4 text-gray-500" />, label: 'Voice Detected', val: session.voice_detected_count },
                                { icon: <AlertTriangle className="w-4 h-4 text-red-500" />, label: 'Phone Detected', val: session.phone_detected_count, highlight: true },
                              ].map(({ icon, label, val, highlight }) => (
                                <div className="flex justify-between text-sm" key={label}>
                                  <span className="flex items-center gap-2">{icon}{label}</span>
                                  <span className={`font-medium ${highlight && val > 0 ? 'text-red-600' : ''}`}>{val || 0}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Session Info */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Session Info</h4>
                            <div className="space-y-2 text-sm">
                              {[
                                { label: 'Session ID', val: session.id?.slice(0, 12) + '...' },
                                { label: 'Batch', val: session.batch_name || 'N/A' },
                                { label: 'Started', val: session.started_at ? new Date(session.started_at).toLocaleString() : 'N/A' },
                                { label: 'Ended', val: session.ended_at ? new Date(session.ended_at).toLocaleString() : 'N/A' },
                                { label: 'Identity Verified', val: session.identity_verified ? 'Yes' : 'No' },
                                { label: 'ID Match Score', val: session.identity_match_score != null ? `${session.identity_match_score}%` : 'N/A' },
                                { label: 'Photos Captured', val: session.photos?.filter(p => p.url).length || 0 },
                                { label: 'Video Clips', val: session.video_clips?.length || 0 },
                              ].map(({ label, val }) => (
                                <div className="flex justify-between" key={label}>
                                  <span className="text-gray-600">{label}</span>
                                  <span className="font-medium">{val}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Evidence / Images */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Evidence & Images</h4>
                            <div className="space-y-2">
                              {session.id_document_url && (
                                <button
                                  onClick={() => setImageModal({ url: session.id_document_url, title: `ID Document — ${session.user_name}` })}
                                  className="w-full flex items-center gap-2 p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm text-blue-700 transition"
                                  data-testid={`evidence-id-doc-${session.id}`}
                                >
                                  <ImageIcon className="w-4 h-4" /> View ID Document
                                  <ExternalLink className="w-3 h-3 ml-auto" />
                                </button>
                              )}
                              {session.live_photo_url && (
                                <button
                                  onClick={() => setImageModal({ url: session.live_photo_url, title: `Live Photo — ${session.user_name}` })}
                                  className="w-full flex items-center gap-2 p-2 bg-green-50 hover:bg-green-100 rounded-lg text-sm text-green-700 transition"
                                  data-testid={`evidence-live-photo-${session.id}`}
                                >
                                  <Camera className="w-4 h-4" /> View Live Photo
                                  <ExternalLink className="w-3 h-3 ml-auto" />
                                </button>
                              )}
                              {/* Random Captured Photos Gallery */}
                              {session.photos && session.photos.length > 0 ? (
                                <div className="space-y-1.5">
                                  <button
                                    onClick={() => openPhotoGallery(session.photos, session.user_name)}
                                    className="w-full flex items-center gap-2 p-2 bg-purple-50 hover:bg-purple-100 rounded-lg text-sm text-purple-700 transition"
                                    data-testid={`evidence-gallery-${session.id}`}
                                  >
                                    <Camera className="w-4 h-4" />
                                    View Captured Photos ({session.photos.filter(p => p.url).length})
                                    <ExternalLink className="w-3 h-3 ml-auto" />
                                  </button>
                                  {/* Thumbnail grid */}
                                  <div className="grid grid-cols-4 gap-1.5">
                                    {session.photos.filter(p => p.url).slice(0, 8).map((photo, idx) => (
                                      <button
                                        key={idx}
                                        onClick={() => setPhotoGallery({ photos: session.photos.filter(p => p.url), currentIndex: idx, title: `Captured Photos — ${session.user_name}` })}
                                        className="relative aspect-square bg-gray-100 rounded overflow-hidden group"
                                      >
                                        <img src={photo.url} alt={`Capture ${idx+1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center">
                                          <Eye className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition" />
                                        </div>
                                        {photo.violations && photo.violations.length > 0 && (
                                          <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full" title={photo.violations.join(', ')} />
                                        )}
                                      </button>
                                    ))}
                                    {session.photos.filter(p => p.url).length > 8 && (
                                      <button
                                        onClick={() => openPhotoGallery(session.photos, session.user_name)}
                                        className="aspect-square bg-gray-100 rounded flex items-center justify-center text-xs text-gray-500 hover:bg-gray-200 transition"
                                      >
                                        +{session.photos.filter(p => p.url).length - 8} more
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic">No captured photos yet</p>
                              )}
                              {!session.id_document_url && !session.live_photo_url && (!session.photos || session.photos.length === 0) && (
                                <p className="text-sm text-gray-400 italic">No evidence images available</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Violation Video Clips */}
                          {session.video_clips && session.video_clips.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <Monitor className="w-4 h-4" /> Violation Video Clips ({session.video_clips.length})
                            </h4>
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {session.video_clips.map((clip, idx) => (
                                <VideoClipPlayer key={clip.id || idx} clip={clip} idx={idx} sessionId={session.id} />
                              ))}
                            </div>
                          </div>
                          )}

                          {/* Dual Video Feeds — Desktop + Mobile */}
                          {(session.full_recording_url || session.mobile_full_recording_url || session.mobile_recordings?.length > 0) && (
                          <div className="mt-4 pt-4 border-t">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <Video className="w-4 h-4" /> Full Recording Feeds
                            </h4>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {/* Desktop recording */}
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                  <Monitor className="w-3 h-3" /> Desktop Camera
                                </p>
                                {session.full_recording_url ? (
                                  <a href={session.full_recording_url} target="_blank" rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-sm">
                                    View Desktop Recording
                                  </a>
                                ) : session.video_recordings?.length > 0 ? (
                                  <p className="text-xs text-gray-500">{session.video_recordings.length} chunk(s) recorded</p>
                                ) : (
                                  <p className="text-xs text-gray-400">No desktop recording</p>
                                )}
                              </div>
                              {/* Mobile recording */}
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                  <Smartphone className="w-3 h-3" /> Mobile Camera (360°)
                                </p>
                                {session.mobile_full_recording_url ? (
                                  <a href={session.mobile_full_recording_url} target="_blank" rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-sm">
                                    View Mobile Recording
                                  </a>
                                ) : session.mobile_recordings?.length > 0 ? (
                                  <p className="text-xs text-gray-500">{session.mobile_recordings.length} chunk(s) recorded</p>
                                ) : (
                                  <p className="text-xs text-gray-400">No mobile recording</p>
                                )}
                              </div>
                            </div>
                            {session.mobile_connected && (
                              <div className="mt-2 flex items-center gap-2 text-xs">
                                <span className="text-green-600 font-medium">Mobile was connected</span>
                                <span className="text-gray-400">|</span>
                                <span className="text-gray-500">Mobile Trust: {Math.round(session.mobile_trust_score || 100)}%</span>
                                <span className="text-gray-400">|</span>
                                <span className="text-gray-500">Combined: {Math.round(session.combined_trust_score || session.trust_score)}%</span>
                              </div>
                            )}
                          </div>
                          )}
                        </div>

                        {/* Violations Timeline */}
                        {session.violations && session.violations.length > 0 && (
                          <div className="mt-6 pt-4 border-t">
                            <h4 className="font-semibold text-gray-900 mb-3">Violations Timeline</h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {session.violations.map((v, idx) => (
                                <div
                                  key={idx}
                                  className={`flex items-center gap-3 p-2 rounded ${
                                    v.severity === 'critical' ? 'bg-red-100' :
                                    v.severity === 'high' ? 'bg-orange-100' :
                                    v.severity === 'medium' ? 'bg-yellow-100' : 'bg-gray-100'
                                  }`}
                                >
                                  {getViolationIcon(v.type)}
                                  <div className="flex-1">
                                    <span className="font-medium text-sm capitalize">{v.type?.replace(/_/g, ' ')}</span>
                                    <span className="text-xs text-gray-500 ml-2">
                                      {v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : ''}
                                    </span>
                                  </div>
                                  <Badge variant="outline" className="text-xs">-{v.trust_penalty}</Badge>
                                  {v.evidence_url && (
                                    <button
                                      onClick={() => setImageModal({ url: v.evidence_url, title: `Violation Evidence — ${v.type?.replace(/_/g, ' ')}` })}
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      <ImageIcon className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Image Viewer Modal */}
      {imageModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setImageModal(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="font-semibold text-gray-900 text-sm">{imageModal.title}</h3>
              <div className="flex items-center gap-2">
                <a href={imageModal.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Open
                </a>
                <button onClick={() => setImageModal(null)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-50" style={{ maxHeight: 'calc(90vh - 60px)' }}>
              <img
                src={imageModal.url}
                alt={imageModal.title}
                className="max-w-full max-h-[70vh] object-contain rounded"
                onError={(e) => { e.target.src = ''; e.target.alt = 'Failed to load image'; }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Modal */}
      {photoGallery && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPhotoGallery(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Gallery Header */}
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="font-semibold text-gray-900 text-sm">{photoGallery.title}</h3>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {photoGallery.currentIndex + 1} / {photoGallery.photos.length}
                </span>
                {photoGallery.photos[photoGallery.currentIndex]?.url && (
                  <a
                    href={photoGallery.photos[photoGallery.currentIndex].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> Open
                  </a>
                )}
                <button onClick={() => setPhotoGallery(null)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Gallery Image */}
            <div className="relative bg-gray-900 flex items-center justify-center" style={{ height: '60vh' }}>
              {photoGallery.photos[photoGallery.currentIndex]?.url ? (
                <img
                  src={photoGallery.photos[photoGallery.currentIndex].url}
                  alt={`Capture ${photoGallery.currentIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.target.alt = 'Failed to load image'; }}
                />
              ) : (
                <p className="text-gray-400">Image not available</p>
              )}
              
              {/* Prev/Next */}
              {photoGallery.photos.length > 1 && (
                <>
                  <button
                    onClick={() => setPhotoGallery(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.photos.length) % prev.photos.length }))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition"
                    data-testid="gallery-prev-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPhotoGallery(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.photos.length }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition"
                    data-testid="gallery-next-btn"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Photo Info */}
            <div className="p-3 border-t bg-gray-50">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  {photoGallery.photos[photoGallery.currentIndex]?.timestamp && (
                    <span className="text-gray-600">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(photoGallery.photos[photoGallery.currentIndex].timestamp).toLocaleString()}
                    </span>
                  )}
                  {photoGallery.photos[photoGallery.currentIndex]?.violations?.length > 0 && (
                    <div className="flex gap-1">
                      {photoGallery.photos[photoGallery.currentIndex].violations.map((v, i) => (
                        <Badge key={i} variant="destructive" className="text-[10px]">{v.replace(/_/g, ' ')}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Thumbnail strip */}
              {photoGallery.photos.length > 1 && (
                <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">
                  {photoGallery.photos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPhotoGallery(prev => ({ ...prev, currentIndex: idx }))}
                      className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition ${
                        idx === photoGallery.currentIndex ? 'border-blue-500' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      {photo.url ? (
                        <img src={photo.url} alt={`Thumb ${idx+1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-[8px] text-gray-400">N/A</div>
                      )}
                      {photo.violations?.length > 0 && (
                        <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-red-500 rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ProctoringDashboard;
